'use client';

import { useQuery } from '@tanstack/react-query';

import { getCourseByIdService, getCourseListService, getLinkableCoursesService } from '@/services/httpServices/CourseServices';
import type {
  CourseByIdResponse,
  CourseListResponse,
  CourseListServiceConfig,
  GetCourseByIdServiceConfig,
  LinkableCoursesResponse,
  LinkableCoursesServiceConfig,
} from '@/services/httpServices/CourseServices/types';

import { courseByIdKeys, courseLinkableKeys, courseListKeys } from './courseQueryKeys';
import { useQueryConfig } from './useQueryConfig';

export function useCourseQuery(params: CourseListServiceConfig) {
  const { isEnabled } = useQueryConfig();

  return useQuery<CourseListResponse>({
    queryKey: courseListKeys(params),
    queryFn: async () => getCourseListService(params),
    enabled: isEnabled,
  });
}

export function useCourseByIdQuery(
  params?: GetCourseByIdServiceConfig,
  enabled?: boolean,
) {
  const { isEnabled } = useQueryConfig();

  return useQuery<CourseByIdResponse>({
    queryKey: courseByIdKeys(params),
    queryFn: async () => {
      if (params === undefined) {
        throw new Error('Course params are required');
      }
      return getCourseByIdService(params);
    },
    enabled: isEnabled && (enabled ?? (params !== undefined && params.params?.params.id !== undefined && params.params?.params.id !== '')),
    refetchOnWindowFocus: 'always',
  });
}

export function useLinkableCoursesQuery(params?: LinkableCoursesServiceConfig) {
  const { isEnabled } = useQueryConfig();

  return useQuery<LinkableCoursesResponse>({
    queryKey: courseLinkableKeys(params),
    queryFn: async () => {
      if (params === undefined) {
        throw new Error('Params are required');
      }
      return getLinkableCoursesService(params);
    },
    enabled: isEnabled && params !== undefined,
  });
}
